/* Softhour data/variant adapter. Curvana's unchanged native components own navigation and zoom. */
const mounted = new WeakMap();
function mount(scope = document) {
  scope.querySelectorAll('[data-sf-native-gallery]').forEach(wrapper => {
    if (mounted.has(wrapper)) return;
    const root = wrapper.closest('[data-sf-product]');
    if (!root) return;
    const abort = new AbortController();
    const templates = new Map([...wrapper.querySelectorAll('template[data-sf-native-album]')].map(t => [t.dataset.sfNativeAlbum, t]));
    const colors = [...root.querySelectorAll(`input[data-sf-option-index="${root.dataset.sfColorOptionIndex}"]`)];
    let disposeAlbum = () => {};
    let warmTimer;
    const warmImages = [];
    function enhance(gallery) {
      if (!gallery) return () => {};
      const cleanup = [];
      const rail = document.createElement('div');
      rail.className = 'cv-gal-swatches'; rail.setAttribute('aria-label', 'Quick color switch');
      colors.forEach(input => {
        const photo = input.closest('label')?.querySelector('.sh-gp-color-photo');
        if (!photo) return;
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'cv-gal-sw';
        button.dataset.color = input.value; button.title = input.value;
        button.setAttribute('aria-label', input.value);
        button.setAttribute('aria-pressed', String(input.checked));
        button.classList.toggle('on', input.checked); button.disabled = input.disabled;
        for (const name of ['background-image', 'background-size', 'background-position']) button.style.setProperty(name, photo.style.getPropertyValue(name));
        button.addEventListener('click', () => {
          if (!input.checked) input.click();
        });
        rail.append(button);
      });
      gallery.append(rail);
      // Small same-photo preview remains beneath the image until the full request decodes.
      gallery.querySelectorAll('slideshow-slide .product-media, .media-gallery__grid .product-media').forEach(media => {
        let img = media.querySelector('img');
        if (!img) return;
        let timer, retry, disposed = false, version = 0;
        const preview = media.dataset.sfPreview;
        if (preview) media.style.backgroundImage = `url(${JSON.stringify(preview)})`;
        function removeRetry() { retry?.remove(); retry = null; }
        function ready() {
          const current = ++version;
          const image = img;
          (image.decode ? image.decode().catch(() => {}) : Promise.resolve()).then(() => {
            if (disposed || current !== version || img !== image || !image.naturalWidth) return;
            delete media.dataset.sfLoading; media.removeAttribute('aria-busy');
            clearTimeout(timer); removeRetry();
          });
        }
        function failed() {
          if (disposed || retry) return;
          clearTimeout(timer); media.setAttribute('aria-busy', 'false');
          retry = document.createElement('button'); retry.type = 'button'; retry.className = 'sf-native-load-error';
          retry.textContent = 'Photo unavailable · Retry';
          retry.addEventListener('click', event => {
            event.stopPropagation(); removeRetry();
            img.removeEventListener('load', ready); img.removeEventListener('error', failed);
            const replacement = img.cloneNode(true);
            const nonce = Date.now();
            const fresh = url => { const u = new URL(url, document.baseURI); u.searchParams.set('_sf_retry', nonce); return u.href; };
            if (img.srcset) replacement.srcset = img.srcset.split(',').map(part => { const [url, width] = part.trim().split(/\s+/); return `${fresh(url)} ${width || ''}`; }).join(', ');
            replacement.src = fresh(img.src); img.replaceWith(replacement); img = replacement; watch();
          });
          media.append(retry);
        }
        function watch() {
          media.dataset.sfLoading = ''; media.setAttribute('aria-busy', 'true');
          img.addEventListener('load', ready); img.addEventListener('error', failed);
          timer = setTimeout(failed, 12000);
          if (img.complete) { if (img.naturalWidth) ready(); else failed(); }
        }
        watch();
        cleanup.push(() => { disposed = true; version++; clearTimeout(timer); img.removeEventListener('load', ready); img.removeEventListener('error', failed); });
      });
      return () => cleanup.forEach(fn => fn());
    }
    function sync() {
      const wanted = wrapper.dataset.selectedColor;
      const current = wrapper.querySelector('[data-album-color]');
      if (current?.dataset.albumColor === wanted) {
        current.querySelectorAll('.cv-gal-sw').forEach(button => {
          const active = button.dataset.color.trim().toLowerCase() === wanted;
          button.classList.toggle('on', active); button.setAttribute('aria-pressed', String(active));
        });
        return;
      }
      const template = templates.get(wanted);
      if (!template || !current) return;
      const focusedColor = current.contains(document.activeElement) ? document.activeElement.closest('.cv-gal-sw')?.dataset.color : null;
      const oldZoom = current.querySelector('zoom-dialog');
      if (oldZoom?.querySelector('dialog')?.open) oldZoom.closeDialog?.();
      disposeAlbum();
      const next = template.content.firstElementChild.cloneNode(true);
      // Upgrade after the complete tree is connected, preserving native ref/index relationships.
      current.replaceWith(next);
      disposeAlbum = enhance(next);
      if (focusedColor) [...next.querySelectorAll('.cv-gal-sw')].find(button => button.dataset.color === focusedColor)?.focus({ preventScroll: true });
    }
    disposeAlbum = enhance(wrapper.querySelector('[data-album-color]'));
    wrapper.addEventListener('sf:variant-media', sync, { signal: abort.signal });
    sync();
    // Prewarm only first photos after initial media; respect data-saver/slow connections.
    if (!navigator.connection?.saveData && !/^(slow-)?2g$/.test(navigator.connection?.effectiveType || '')) {
      warmTimer = setTimeout(() => templates.forEach(template => {
        const source = template.content.querySelector('.media-gallery__grid img');
        if (!source) return;
        const image = new Image(); image.fetchPriority = 'low'; image.decoding = 'async';
        image.sizes = source.sizes; image.srcset = source.srcset; image.src = source.src; warmImages.push(image);
      }), 1500);
    }
    mounted.set(wrapper, () => { abort.abort(); disposeAlbum(); clearTimeout(warmTimer); mounted.delete(wrapper); });
  });
}
mount();
document.addEventListener('shopify:section:load', event => mount(event.target));
document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-sf-native-gallery]').forEach(wrapper => mounted.get(wrapper)?.()));
